import { useState } from "react";
import { ArrowRight, Play } from "lucide-react";
import { ServiceRevOpsMarquee } from "./service-revops-marquee";
import { gohighlevelConfig as defaultConfig } from "./service-platform-config";
import type { PlatformConfig } from "./service-platform-config";

function VideoPanel({
  videoSrc,
  posterSrc,
  alt,
  playable = true,
}: {
  videoSrc: string;
  posterSrc: string;
  alt: string;
  playable?: boolean;
}) {
  const [playing, setPlaying] = useState(false);

  // On pages without a real video (every service page except the flagship
  // GoHighLevel page), the poster + play button stay purely visual: clicking
  // does NOT swap to the shared placeholder video.
  const showVideo = playable && playing;

  return (
    <div
      className="relative z-10 w-full rounded-[24px] overflow-hidden group"
      style={{
        aspectRatio: "16 / 9",
        background: "#EAF5FF",
        border: "1px solid rgba(8,46,103,0.08)",
        boxShadow: "0 20px 58px rgba(8,46,103,0.13)",
      }}
    >
      {/* Soft blue aura behind the frame */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-6 -z-10 rounded-[36px] blur-2xl"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 40%, rgba(11,124,242,0.18), transparent 72%)",
        }}
      />

      {showVideo ? (
        <video
          src={videoSrc}
          poster={posterSrc}
          autoPlay
          controls
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          role={playable ? "button" : "img"}
          tabIndex={playable ? 0 : undefined}
          onClick={playable ? () => setPlaying(true) : undefined}
          onKeyDown={
            playable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setPlaying(true);
                  }
                }
              : undefined
          }
          aria-label={
            playable
              ? "Play the system architecture walkthrough"
              : "System architecture walkthrough preview"
          }
          className="absolute inset-0 w-full h-full focus:outline-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[rgba(11,124,242,0.40)] focus-visible:outline-offset-4"
        >
          <img
            src={posterSrc}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          {/* Bottom gradient for label contrast */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(5,25,67,0.6) 0%, rgba(5,25,67,0.12) 40%, transparent 62%)",
            }}
          />
          {/* Play button — always visible; only triggers playback when playable */}
          <span className="absolute inset-0 flex items-center justify-center">
            <span
              className="flex items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-105"
              style={{
                width: 74,
                height: 74,
                background: "linear-gradient(135deg, #0B7CF2, #118FF7)",
                boxShadow: "0 10px 26px rgba(11,124,242,0.28)",
              }}
            >
              <Play
                className="w-8 h-8 text-white translate-x-0.5"
                style={{ fill: "white" }}
              />
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

export function ServiceRevOpsHero({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  return (
    <section className="revops-hero">
      {/* Radial glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(60,190,255,0.10) 0%, rgba(60,190,255,0.04) 38%, transparent 72%)",
        }}
      />
      {/* Subtle bottom wave — connects to next section */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 z-0"
        style={{ background: "linear-gradient(to top, #E5F5FF, transparent)" }}
      />

      <div className="revops-hero-inner">
        {/* Eyebrow */}
        <div
          className="revops-hero-eyebrow inline-flex items-center gap-2 font-sans font-semibold uppercase"
          style={{ fontSize: "11px", letterSpacing: "0.2em", color: "#0B7CF2" }}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: "#25DD5C" }}
          />
          {config.eyebrow}
        </div>

        {/* Headline — 2-line color split, dominant element */}
        <h1 className="revops-hero-h1" style={{ color: "#071E61" }}>
          <span className="block">{config.heroHeadlineNavy}</span>
          <span className="block" style={{ color: "#0B7CF2" }}>
            {config.heroHeadlineBlue}
          </span>
        </h1>

        {/* Author line (E-E-A-T) */}
        <div
          className="revops-hero-author flex flex-wrap items-center justify-center gap-2 font-sans"
          style={{ fontSize: "13px", color: "#536B89" }}
        >
          <span className="font-bold" style={{ color: "#082E67" }}>
            By Marjohn Robillo
          </span>
          <span style={{ color: "rgba(8,46,103,0.3)" }}>·</span>
          <span>{config.heroAuthorRole}</span>
          <span style={{ color: "rgba(8,46,103,0.3)" }}>·</span>
          <a
            href="https://www.linkedin.com/in/marjohnrobillo-com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:underline"
            style={{ color: "#0B7CF2" }}
          >
            LinkedIn
          </a>
        </div>

        {/* AEO answer-first paragraph — 2 clean balanced lines */}
        <p className="revops-hero-para" style={{ color: "#536B89" }}>
          <span className="block sm:inline">{config.heroPara1}</span>{" "}
          <span className="block sm:inline">{config.heroPara2}</span>
        </p>

        {/* Compact centered video — supporting proof */}
        <div className="revops-hero-video-wrap">
          {/* Giant pale ghost word behind the video */}
          <div aria-hidden="true" className="revops-hero-ghost">
            {config.ghostWord}
          </div>
          <VideoPanel
            videoSrc={config.heroVideoSrc}
            posterSrc={config.heroVideoPoster}
            alt={config.heroVideoAlt}
            playable={config.heroVideoPlayable ?? false}
          />
          {/* Handwritten annotation (desktop only, decorative) */}
          <div
            aria-hidden="true"
            className="pointer-events-none hidden sm:flex absolute -right-2 -top-7 items-center gap-1 rotate-[8deg]"
          >
            <span
              style={{
                fontFamily: "'Caveat', cursive",
                fontSize: "22px",
                color: "#0B7CF2",
                fontWeight: 600,
              }}
            >
              Watch it work
            </span>
            <svg
              width="34"
              height="28"
              viewBox="0 0 34 28"
              fill="none"
              style={{ transform: "rotate(-6deg)" }}
            >
              <path
                d="M2 3 C 10 1, 22 4, 30 17"
                stroke="#0B7CF2"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M30 17 L 23 13 M30 17 L 24 21"
                stroke="#0B7CF2"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        {/* CTA + microcopy — placed below the video */}
        <div className="revops-hero-cta-row flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/book"
            className="group inline-flex items-center gap-2 rounded-full font-sans font-bold text-white transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A7FF]"
            style={{
              background: "linear-gradient(135deg, #0B7CF2, #118FF7)",
              height: 56,
              padding: "0 32px",
              fontSize: "15px",
              boxShadow: "0 10px 26px rgba(11,124,242,0.20)",
            }}
          >
            {config.heroCtaLabel}
            <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <p
            className="font-sans"
            style={{ fontSize: "12.5px", color: "#7A8DA8" }}
          >
            30 min · Free · No commitment
          </p>
        </div>

        {/* Trust social-proof header introducing the production platforms marquee */}
        <div className="revops-hero-trust-header flex flex-col items-center justify-center text-center">
          <div className="inline-flex items-center justify-center gap-2.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{
                background: "#25DD5C",
                boxShadow: "0 0 0 3.5px rgba(37, 221, 92, 0.20)",
              }}
            />
            <span
              className="font-sans font-bold uppercase tracking-[0.14em]"
              style={{
                fontSize: "clamp(12.5px, 1.1vw, 14px)",
                color: "#0B7CF2",
                letterSpacing: "0.14em",
              }}
            >
              {config.heroTrustHeader}
            </span>
          </div>
        </div>

        {/* Live-proof marquee — infinite right-to-left case-study cards.
            Sits inside the hero as the active proof engine (trusted badge). */}
        <div className="revops-hero-marquee">
          <ServiceRevOpsMarquee />
        </div>
      </div>
    </section>
  );
}
